import { useContext, useEffect, useState } from "react";
import { useNavigate, useParams } from "react-router-dom";
import Alert from "@mui/material/Alert";
import Box from "@mui/material/Box";
import Button from "@mui/material/Button";
import CircularProgress from "@mui/material/CircularProgress";
import Stack from "@mui/material/Stack";
import Typography from "@mui/material/Typography";
import AuthContext from "../store/auth-context";

export default function SharedEnvironmentalFeedPage() {
  const { candidateId } = useParams<{ candidateId: string }>();
  const navigate = useNavigate();
  const authCtx = useContext(AuthContext);
  const [error, setError] = useState<string | null>(null);

  useEffect(() => {
    if (!candidateId) {
      setError("El enlace no identifica una publicación ambiental.");
      return;
    }

    if (authCtx.isLoggedIn) {
      navigate(`/environmental-feed?publication=${encodeURIComponent(candidateId)}`, { replace: true });
      return;
    }

    let cancelled = false;
    const createAnonymousSession = async () => {
      try {
        const response = await fetch(`${process.env.REACT_APP_API_HOST ?? ""}/auth/anonymous`, {
          method: "POST",
          headers: { Accept: "application/json" },
        });
        if (!response.ok) {
          throw new Error("No se pudo crear el acceso anónimo para abrir la publicación.");
        }
        const payload = await response.json();
        if (cancelled) return;
        const lifetimeSeconds = Number(payload.expiresIn) || 21600;
        const expirationTime = new Date(Date.now() + lifetimeSeconds * 1000);
        authCtx.login(
          payload.access_token,
          expirationTime.toISOString(),
          payload.userEmail || payload.displayName
        );
        navigate(`/environmental-feed?publication=${encodeURIComponent(candidateId)}`, { replace: true });
      } catch (caught) {
        if (!cancelled) {
          setError(caught instanceof Error ? caught.message : "No se pudo abrir la publicación compartida.");
        }
      }
    };

    void createAnonymousSession();
    return () => {
      cancelled = true;
    };
  }, [authCtx, authCtx.isLoggedIn, candidateId, navigate]);

  return (
    <Box sx={{ minHeight: "100vh", display: "grid", placeItems: "center", p: 3 }}>
      <Stack spacing={2} alignItems="center" sx={{ maxWidth: 440, textAlign: "center" }}>
        {error ? (
          <>
            <Alert severity="error">{error}</Alert>
            <Button variant="contained" onClick={() => navigate("/")}>Volver</Button>
          </>
        ) : (
          <>
            <CircularProgress />
            <Typography variant="h6">Abriendo publicación ambiental</Typography>
            <Typography variant="body2" color="text.secondary">
              Protea está creando un acceso anónimo temporal para mostrar el feed.
            </Typography>
          </>
        )}
      </Stack>
    </Box>
  );
}
